/**
 * brand.ts — Whitelabel Phase B (2026-07-19)
 *
 * Per-tenant brand for the SPA, injected at container start by nginx envsubst
 * on /config.js → window.__VIBE_CONFIG__ (the same runtime-config rail as the
 * per-tenant OIDC config — a shared image serves all tenants, so build-time
 * VITE_* can't be per-tenant). String fields arrive base64'd (s02 _brand_b64)
 * so a brand value containing ' / # / " can't break the JS string literal;
 * decode here with a guard. Absent or malformed → the gamemagick defaults, so
 * nothing regresses for existing tenants or local Vite dev.
 */

const DEFAULTS = {
  displayName: 'GameMagick AI',
  productNameVibe: 'Vibe Designer',
  colorPrimary: '#6366f1',
  colorAccent: '#8b5cf6',
  faviconUrl: '',
  // Empty = "no logo": consumers render their icon-only markup, so unbranded
  // tenants stay pixel-identical (brand-logo arc 2026-07-20).
  logoUrl: '',
} as const;

export interface Brand {
  displayName: string;
  productNameVibe: string;
  colorPrimary: string;
  colorAccent: string;
  faviconUrl: string;
  logoUrl: string;
}

function decodeB64(value: string | undefined, fallback: string): string {
  if (!value) return fallback;
  try {
    return atob(value) || fallback;
  } catch {
    // Malformed base64 (should never happen — s02 encodes it) → never crash
    // the SPA over branding; fall back to the default.
    return fallback;
  }
}

// Evenstar SSO-login branding (2026-08-04, plan docs/20260804/20260804_130300
// _PLAN_EVENSTAR_SSO_LOGIN_BRANDING.md): `*.evenstarfoundry.com` is a public
// equal apex (E1 multi-apex) served by this same per-tenant container, and the
// Evenstar brand has a zero-arch-hints policy — platform-default GameMagick
// labels on those hosts are a leak. The guard is VALUE-based, not
// absence-based: the rendered /config.js explicitly carries the default
// display name (b64 "GameMagick AI") for unbranded tenants, so "absent →
// override" would never fire; and a tenant with an explicit non-default brand
// (dojo/Black Belt Brain) must keep it on every host.
export const EVENSTAR_APEX_SUFFIX = '.evenstarfoundry.com';
export const EVENSTAR_DISPLAY_NAME = 'Evenstar Foundry';

/**
 * Read the current tenant's brand from window.__VIBE_CONFIG__ (evaluated at
 * call time so tests can stub the global). Cheap — safe to call in render.
 * `hostname` is a parameter for testability (jsdom's location is immutable);
 * production call sites pass nothing.
 */
export function getBrand(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''
): Brand {
  const cfg = (typeof window !== 'undefined' && window.__VIBE_CONFIG__) || {};
  const displayName = decodeB64(cfg.brand_display_name_b64, DEFAULTS.displayName);
  return {
    displayName:
      hostname.endsWith(EVENSTAR_APEX_SUFFIX) && displayName === DEFAULTS.displayName
        ? EVENSTAR_DISPLAY_NAME
        : displayName,
    productNameVibe: decodeB64(cfg.brand_product_name_vibe_b64, DEFAULTS.productNameVibe),
    colorPrimary: cfg.brand_color_primary || DEFAULTS.colorPrimary,
    colorAccent: cfg.brand_color_accent || DEFAULTS.colorAccent,
    faviconUrl: decodeB64(cfg.brand_favicon_url_b64, DEFAULTS.faviconUrl),
    logoUrl: decodeB64(cfg.brand_logo_url_b64, DEFAULTS.logoUrl),
  };
}
